import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { authAPI } from '../services/api.ts';
import {
  Alert,
  Button,
  Center,
  Paper,
  PasswordInput,
  Stack,
  Text,
  Title,
} from '@mantine/core';

export const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token');

  if (!token) {
    return (
      <Center mih="100vh" p="md">
        <Paper withBorder shadow="md" p="xl" w="100%" maw={420}>
          <Stack gap="lg">
            <Stack gap={4}>
              <Title order={3} ta="center">
                Invalid Reset Link
              </Title>
              <Text size="sm" c="dimmed" ta="center">
                This password reset link is invalid or has expired.
              </Text>
            </Stack>
            <Button component={Link} to="/forgot-password" fullWidth>
              Request New Reset Link
            </Button>
          </Stack>
        </Paper>
      </Center>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate password
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    // Validate password match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      await authAPI.resetPassword(token, password);
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <Center mih="100vh" p="md">
        <Paper withBorder shadow="md" p="xl" w="100%" maw={420}>
          <Stack gap="lg">
            <Stack gap={4}>
              <Title order={3} ta="center">
                Password Reset Successful
              </Title>
              <Text size="sm" c="dimmed" ta="center">
                Your password has been successfully reset. You will be
                redirected to the login page shortly.
              </Text>
            </Stack>
            <Button component={Link} to="/login" fullWidth>
              Go to Login
            </Button>
          </Stack>
        </Paper>
      </Center>
    );
  }

  return (
    <Center mih="100vh" p="md">
      <Paper withBorder shadow="md" p="xl" w="100%" maw={420}>
        <Stack gap={4} mb="lg">
          <Title order={2} ta="center">
            Reset Password
          </Title>
          <Text size="sm" c="dimmed" ta="center">
            Please enter your new password
          </Text>
        </Stack>

        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            {error && (
              <Alert color="red" variant="light">
                {error}
              </Alert>
            )}

            <PasswordInput
              id="password"
              label="New Password"
              placeholder="Enter new password"
              required
              withAsterisk={false}
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              visible={showPassword}
              onVisibilityChange={setShowPassword}
            />

            <PasswordInput
              id="confirmPassword"
              label="Confirm New Password"
              placeholder="Confirm new password"
              required
              withAsterisk={false}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.currentTarget.value)}
              visible={showConfirmPassword}
              onVisibilityChange={setShowConfirmPassword}
            />

            <Button type="submit" fullWidth loading={isLoading}>
              Reset Password
            </Button>
          </Stack>
        </form>
      </Paper>
    </Center>
  );
};
